/**
 * Cuadres: each account that has a detail of its own, against that detail.
 *
 * Cuentas por cobrar is also every client's balance; cuentas por pagar, what is still owed on each
 * purchase; inventory, what is on the shelves at its average cost. The journal and those tables
 * are written by the same reducer, in the same step, from the same event, so they can only
 * disagree when a reducer posts one and forgets the other. Nothing else would notice: the balance
 * sheet still squares, the client's page still shows a balance, and they no longer describe the
 * same money (`AUDITORIAS.md` item 5).
 *
 * Plain data in, so the phone, the server and the tests line up the same figures the same way.
 * Each detail is optional: a caller that has not loaded one gets no line for it.
 *
 * Every pair must agree to the centavo but stock. Each movement books its cost rounded to the
 * centavo, and the shelves are valued once, at an average kept to a thousandth of a centavo, so
 * the two drift by rounding: a centavo here and there, never more than one for each posting to a
 * stock account and one for each stock line — and, where an average was worked out again over
 * thousands of units, what its last thousandth of a centavo comes to over that many: half a
 * centavo every thousand units of the line (`tolerancia`, `redondeoDelPromedio`). Nine thousand
 * pounds of sugar drifted five centavos on one purchase, and the books were called wrong
 * (2026-10-13). Past that it is not rounding.
 */

import { COST_SCALE, MILLI_SCALE, addMoney, costOf, subMoney, type Centavos } from '@abastece/shared';
import { cuentaInventario } from '../inventario.js';
import { efectivoEsperado } from '../projections/caja.js';
import { ACCOUNTS } from '../ledger/accounts.js';
import type { JournalEntry } from '../ledger/journal.js';
import {
  pendienteDeCompra,
  pendienteDeGasto,
  type Abono,
  type Cliente,
  type Compra,
  type Empleado,
  type Existencia,
  type Gasto,
  type PagoCotizaciones,
  type Planilla,
  type Recarga,
  type SaldoTelefonico,
  type SesionCaja,
  type Venta,
} from '../state.js';

/** What an account is lined up against, for the screen to name. */
export type DetalleCuadre =
  | 'gaveta'
  | 'clientes'
  | 'proveedores'
  | 'inventario'
  | 'camion'
  | 'transformacion'
  | 'porRecibir'
  | 'dolares'
  | 'transferencias'
  | 'saldoTelefonico'
  | 'adelantos'
  | 'aguinaldo'
  | 'vacaciones'
  | 'sueldos'
  | 'inss'
  | 'inatec'
  | 'ir';

export interface Cuadre {
  cuenta: string;
  detalle: DetalleCuadre;
  /** The account's balance, by its normal side: what the books say. */
  libros: Centavos;
  /** What the detail adds up to. */
  suma: Centavos;
  /** Libros − suma. */
  diferencia: Centavos;
  /** How far apart rounding alone can put them: zero for everything but stock. */
  tolerancia: Centavos;
}

/** Whether the two agree, rounding allowed. Otherwise a reducer wrote one and not the other. */
export const cuadra = (c: Pick<Cuadre, 'diferencia' | 'tolerancia'>): boolean => Math.abs(c.diferencia) <= c.tolerancia;

const CUENTAS_INVENTARIO = new Set([ACCOUNTS.INVENTARIO.code, ACCOUNTS.INVENTARIO_CAMION.code, ACCOUNTS.INVENTARIO_TRANSFORMACION.code]);

/** How many journal lines moved each stock account: each can carry half a centavo of rounding. */
export function lineasDeInventario(asientos: Iterable<Pick<JournalEntry, 'lineas'>>): Map<string, number> {
  const out = new Map<string, number>();
  for (const a of asientos) {
    for (const l of a.lineas) if (CUENTAS_INVENTARIO.has(l.cuenta)) out.set(l.cuenta, (out.get(l.cuenta) ?? 0) + 1);
  }
  return out;
}

export interface DatosCuadres {
  /** Each account's balance by code, signed by its normal side (`movimientos` over every entry). */
  saldos: ReadonlyMap<string, number>;
  /** Journal lines on each stock account (`lineasDeInventario`), for how far rounding reaches. */
  lineasInventario?: ReadonlyMap<string, number>;
  clientes?: Iterable<Pick<Cliente, 'saldo'>>;
  compras?: Iterable<Pick<Compra, 'formaPago' | 'anulada' | 'totalCordobas' | 'pagado' | 'devueltoACuenta'>>;
  gastos?: Iterable<Pick<Gasto, 'formaPago' | 'anulado' | 'montoCordobas' | 'pagado'>>;
  existencias?: Iterable<Pick<Existencia, 'ubicacion' | 'cantidad' | 'costoPromedio'>>;
  sesiones?: Iterable<
    Pick<
      SesionCaja,
      | 'id'
      | 'estado'
      | 'relevoDe'
      | 'esperadoApertura'
      | 'deGeneral'
      | 'queda'
      | 'fondoInicial'
      | 'efectivoCordobas'
      | 'cambioEntregado'
      | 'retiros'
      | 'ingresos'
      | 'abonos'
      | 'devoluciones'
      | 'pagado'
      | 'ajusteCordobas'
      | 'porRecibir'
      | 'porRecibirValor'
      | 'dolaresContables'
    >
  >;
  /** Sales with a payment still waiting in the transfer queue; any others are ignored. */
  ventas?: Iterable<Pick<Venta, 'anulada' | 'pagos'>>;
  recargas?: Iterable<Pick<Recarga, 'estadoPago' | 'montoFacial'>>;
  /** Abonos whose transfer or wallet payment still waits in the queue; any others are ignored. */
  abonos?: Iterable<Pick<Abono, 'estadoPago' | 'anulado' | 'monto'>>;
  saldosTelefonicos?: Iterable<Pick<SaldoTelefonico, 'costo'>>;
  empleados?: Iterable<Pick<Empleado, 'adelantosPendientes' | 'aguinaldoAcumulado' | 'vacacionesAcumuladas'>>;
  planillas?: Iterable<
    Pick<Planilla, 'pagada' | 'totalNeto' | 'totalAdelantos' | 'totalInssLaboral' | 'totalInssPatronal' | 'totalInatec' | 'totalIr'>
  >;
  pagosCotizaciones?: Iterable<Pick<PagoCotizaciones, 'inss' | 'inatec' | 'ir'>>;
}

/**
 * What working an average out again can move a line's value by, at most, in whole centavos: the
 * average is kept to a thousandth of a centavo a unit, so half of that over every unit on the line.
 */
export function redondeoDelPromedio(cantidad: number): number {
  return Math.ceil(Math.abs(cantidad) / (2 * MILLI_SCALE * COST_SCALE));
}

function sumar<T>(xs: Iterable<T>, f: (x: T) => number): Centavos {
  let s = 0;
  for (const x of xs) s += f(x);
  return s as Centavos;
}

export function cuadres(d: DatosCuadres): Cuadre[] {
  const out: Cuadre[] = [];
  const linea = (cuenta: string, detalle: DetalleCuadre, suma: Centavos, tolerancia = 0) => {
    const libros = (d.saldos.get(cuenta) ?? 0) as Centavos;
    out.push({ cuenta, detalle, libros, suma, diferencia: subMoney(libros, suma), tolerancia: tolerancia as Centavos });
  };

  if (d.clientes) linea(ACCOUNTS.CUENTAS_POR_COBRAR.code, 'clientes', sumar(d.clientes, (c) => c.saldo ?? 0));

  if (d.compras || d.gastos) {
    linea(
      ACCOUNTS.CUENTAS_POR_PAGAR.code,
      'proveedores',
      addMoney(
        sumar(d.compras ?? [], (c) => pendienteDeCompra(c as Compra)),
        sumar(d.gastos ?? [], pendienteDeGasto),
      ),
    );
  }

  if (d.existencias) {
    const valor = new Map<string, number>();
    const filas = new Map<string, number>();
    for (const e of d.existencias) {
      if (e.cantidad === 0) continue;
      const cuenta = ACCOUNTS[cuentaInventario(e.ubicacion)].code;
      valor.set(cuenta, (valor.get(cuenta) ?? 0) + costOf(e.costoPromedio, e.cantidad));
      filas.set(cuenta, (filas.get(cuenta) ?? 0) + 1 + redondeoDelPromedio(e.cantidad));
    }
    const stock = (cuenta: string, detalle: DetalleCuadre) =>
      linea(cuenta, detalle, (valor.get(cuenta) ?? 0) as Centavos, (d.lineasInventario?.get(cuenta) ?? 0) + (filas.get(cuenta) ?? 0));
    stock(ACCOUNTS.INVENTARIO.code, 'inventario');
    stock(ACCOUNTS.INVENTARIO_CAMION.code, 'camion');
    stock(ACCOUNTS.INVENTARIO_TRANSFORMACION.code, 'transformacion');
  }

  if (d.sesiones) {
    const sesiones = [...d.sesiones];
    /*
     * The drawers' córdobas (2026-10-15): what each open till should be holding, and what each
     * closed one left that no shift has taken over yet. Once one opened on it, that shift's count
     * holds what was left. A count corrected moves both or neither. Less what an opening was held
     * against that no corte in these books left (`esperado` with no `relevoDe`): that was never on
     * them, and stays apart from them for good. The app names the corte it takes over, so on a
     * shop's books that is nothing; a test that starts the day with last night's float has it.
     */
    const relevadas = new Set(sesiones.map((s) => s.relevoDe).filter((x): x is string => Boolean(x)));
    const tiene = (s: (typeof sesiones)[number]) => (s.estado === 'abierta' ? efectivoEsperado(s as SesionCaja) : relevadas.has(s.id) ? 0 : (s.queda ?? 0));
    const sinCorte = (s: (typeof sesiones)[number]) => (s.relevoDe ? 0 : Math.max((s.esperadoApertura ?? 0) - (s.deGeneral ?? 0), 0));
    linea(ACCOUNTS.CAJA_CORDOBAS.code, 'gaveta', sumar(sesiones, (s) => tiene(s) - sinCorte(s)));
    linea(
      ACCOUNTS.EFECTIVO_POR_RECIBIR.code,
      'porRecibir',
      sumar(sesiones, (s) => (s.porRecibir ?? 0) + (s.porRecibirValor ?? 0)),
    );
    linea(
      ACCOUNTS.CAJA_DOLARES.code,
      'dolares',
      sumar(sesiones, (s) => (s.estado === 'abierta' ? (s.dolaresContables ?? 0) : 0)),
    );
  }

  if (d.ventas || d.recargas || d.abonos) {
    const enVentas = sumar(d.ventas ?? [], (v) =>
      v.anulada ? 0 : v.pagos.reduce((a, p) => (p.estado === 'pendiente' ? a + p.montoContable : a), 0),
    );
    const enRecargas = sumar(d.recargas ?? [], (r) => (r.estadoPago === 'pendiente' ? r.montoFacial : 0));
    const enAbonos = sumar(d.abonos ?? [], (a) => (a.estadoPago === 'pendiente' && !a.anulado ? a.monto : 0));
    linea(ACCOUNTS.TRANSFERENCIAS_PENDIENTES.code, 'transferencias', addMoney(enVentas, enRecargas, enAbonos));
  }

  if (d.saldosTelefonicos) {
    linea(ACCOUNTS.SALDO_TELEFONICO.code, 'saldoTelefonico', sumar(d.saldosTelefonicos, (s) => s.costo));
  }

  if (d.empleados) {
    const empleados = [...d.empleados];
    linea(ACCOUNTS.ADELANTOS_EMPLEADOS.code, 'adelantos', sumar(empleados, (e) => e.adelantosPendientes ?? 0));
    linea(ACCOUNTS.AGUINALDO_POR_PAGAR.code, 'aguinaldo', sumar(empleados, (e) => e.aguinaldoAcumulado));
    linea(ACCOUNTS.VACACIONES_POR_PAGAR.code, 'vacaciones', sumar(empleados, (e) => e.vacacionesAcumuladas));
  }

  if (d.planillas) {
    const planillas = [...d.planillas];
    const pagos = [...(d.pagosCotizaciones ?? [])];
    linea(
      ACCOUNTS.SUELDOS_POR_PAGAR.code,
      'sueldos',
      sumar(planillas, (p) => (p.pagada ? 0 : p.totalNeto - (p.totalAdelantos ?? 0))),
    );
    linea(
      ACCOUNTS.INSS_POR_PAGAR.code,
      'inss',
      subMoney(sumar(planillas, (p) => p.totalInssLaboral + p.totalInssPatronal), sumar(pagos, (x) => x.inss)),
    );
    linea(
      ACCOUNTS.INATEC_POR_PAGAR.code,
      'inatec',
      subMoney(sumar(planillas, (p) => p.totalInatec), sumar(pagos, (x) => x.inatec)),
    );
    linea(
      ACCOUNTS.IR_RETENIDO_POR_PAGAR.code,
      'ir',
      subMoney(sumar(planillas, (p) => p.totalIr), sumar(pagos, (x) => x.ir)),
    );
  }

  return out;
}
